import type { Ref, App, ComputedRef } from 'vue'
import { ref, unref, createApp, computed, watch, onBeforeUnmount } from 'vue'
import type { I18n } from 'vue-i18n'
import { useI18n } from 'vue-i18n'
import type { Instance, AnnotationsUnion, List } from 'pspdfkit'
import type PSPDFKitType from 'pspdfkit'

import type { AnnotationType } from '@lib/components/CFileViewer/CFileViewer.types'
import type { PitcherFile } from '@lib/types/file.types'
import type { MiniPage, File, FileRetrieve } from '@openapi'
import useThemeVars from '@lib/composables/useThemeVars'
import { CLIENT_TYPE } from '@lib/constants/cdp.const'
import { loadScriptStyle } from '@lib/util/dom'
import useFileViewerFullscreen from '@lib/components/CFileViewer/pdf/CFileViewerFullscreen.pdf.use'
import CFileViewerSearchResultsSidebar from '@lib/components/CFileViewer/pdf/CFileViewerSearchResultsSidebar.pdf.vue'
import type CFileViewerBottomPdf from '@lib/components/CFileViewer/pdf/CFileViewerBottom.pdf.vue'
import usePageThumbnailMetadata from '@lib/components/CFileViewer/pdf/CFileViewer.pageThumbMetadata.pdf.use'
import useConfirmation from '@lib/composables/useConfirmation'
import type { CanvasFile } from '@canvas-builder/types/canvas'
import useFileViewerSettings from '@lib/composables/fileViewerSettings.use'
import type { SupportedLocale } from '@lib/types/i18n.types'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types'
import type { InstanceSettings } from '@lib/types/instanceSettings.types'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import { useLinkAnnotation } from '@lib/components/CFileViewer/pdf/CFileViewer.pdf.link.use'
import type { SaveLinkAnnotationPayload } from '@lib/components/CFileViewer/pdf/CFileViewer.pdf.link.types'
import { jwtDecode } from '@lib/util/jwt'
import { useAnnotations } from '@lib/components/CFileViewer/pdf/CFileViewer.pdf.annotations.use'
import { useOverlayItems } from '@lib/components/CFileViewer/pdf/CFileViewer.pdf.overlayItems.use'
import { exitFullscreen } from '@lib/util/fullscreen.util'

type PSPDFKitAuthPayload = {
  jwt: string
}

const { fileViewerSettings, updateFileViewerSettings } = useFileViewerSettings()
interface FileViewerActions {
  onCloseFile: () => void
  onShowPageMetadata: (pageIndex: number) => void
}
interface FileViewerPdfReturnType {
  viewPdf: (file: PitcherFile | CanvasFile | null, pageIndex: MiniPage['page_index']) => Promise<Instance | void>
  onPdfFullscreen: (force?: boolean) => void
  updateFileViewerSettings: (settings: Partial<typeof fileViewerSettings.value>) => void
  cleanupPspdfKit: () => void
  resetAnnotationChanges: () => void
  isSearchOpen: Ref<boolean>
  isThumbnailsOpen: Ref<boolean>
  isTextAnnotationOpen: Ref<boolean>
  isLinkAnnotationOpen: Ref<boolean>
  currentPageIndex: Ref<number>
  isAnnotationOpen: Ref<boolean>
  areAnnotationsModified: Ref<boolean>
  selectedAnnotationType: Ref<Exclude<AnnotationType, 'text'>>
  isSwitchingInteractionMode: Ref<boolean>
  fileViewerSettings: typeof fileViewerSettings
  isFullscreen: Ref<boolean>
  currentLinkAnnotation: Ref<AnnotationsUnion | null>
  showLinkAnnotationPopover: ComputedRef<boolean>
  onToggleLinkAnnotation: () => void
  onCloseLinkAnnotationPopover: () => void
  onSaveLinkAnnotation: (data: SaveLinkAnnotationPayload) => void
  hasHotspotsOnCurrentPage: Ref<boolean>
}

export default ({
  pdfViewerEl,
  bottomBarRef,
  i18n,
  appLocale,
  actions,
  assetsPrefix,
  clientType,
  context,
  pitcherSettings,
  launchDarkly,
  emit,
  fileId,
  fetchFileById,
}: {
  pdfViewerEl: Ref<HTMLElement | null>
  bottomBarRef: Ref<InstanceType<typeof CFileViewerBottomPdf> | null>
  i18n: I18n
  appLocale?: SupportedLocale
  actions: FileViewerActions
  assetsPrefix: string
  clientType: (typeof CLIENT_TYPE)[keyof typeof CLIENT_TYPE]
  context?: Record<string, any>
  pitcherSettings?: ComputedRef<OrganizationSettings & InstanceSettings>
  launchDarkly: ComputedRef<LaunchDarklyEnv>
  fileId: ComputedRef<File['id'] | undefined>
  emit: (name: string, ...args: any[]) => void
  fetchFileById?: (id: FileRetrieve['id']) => Promise<FileRetrieve>
}): FileViewerPdfReturnType => {
  const { t } = useI18n()
  const themeVars = useThemeVars()

  // PDF Section Start

  const instance = ref<Instance>()
  const isSearchOpen = ref(false)
  const isThumbnailsOpen = ref(false)
  const isTextAnnotationOpen = ref(false)
  const isLinkAnnotationOpen = ref(false)
  const isAnnotationOpen = ref(false)
  const selectedAnnotationType = ref<Exclude<AnnotationType, 'text'>>('drawing')
  const isSwitchingInteractionMode = ref(false)
  const currentPageIndex = ref(0)
  const isCurrentPageChanged = ref(false)
  const isFullscreen = ref(false)

  const confirmation = useConfirmation()
  const linkAnnotationData = useLinkAnnotation({
    instance,
    isLinkAnnotationOpen,
    isAnnotationOpen,
    isThumbnailsOpen,
    isSearchOpen,
    isTextAnnotationOpen,
  })
  const {
    areAnnotationsModified,
    updateAnnotations,
    interpolateAnnotations,
    cleanAnnotations,
    getAllAnnotations,
    resetAnnotationChanges,
  } = useAnnotations(instance, {
    clientType,
    currentLinkAnnotation: linkAnnotationData.currentLinkAnnotation,
    currentFileId: fileId,
    currentFilePageIndex: computed(() => currentPageIndex.value),
    emit,
  })
  const { setOverlayItemsForEmbeddedVideos } = useOverlayItems({ fetchFileById, instance: instance })

  function queryShadowDom(selector: string, root: Document | Element | ShadowRoot = document): Element[] {
    const elements = Array.from(root.querySelectorAll(selector))
    const shadowRoots = Array.from(root.querySelectorAll('*'))
      .map((el) => el.shadowRoot)
      .filter(Boolean) as ShadowRoot[]

    const shadowElements = shadowRoots.flatMap((shadowRoot) => queryShadowDom(selector, shadowRoot))

    return [...elements, ...shadowElements]
  }

  function setAnnotationToolbarItems(instance: Instance) {
    instance.setAnnotationToolbarItems((_, { defaultAnnotationToolbarItems }) => {
      const buttonNode = document.createElement('button')
      buttonNode.textContent = t('canvasUI.components.fileViewer.deleteAnnotationsButtonText')
      buttonNode.className = 'PSPDFKit-Toolbar-Button PSPDFKit-Tool-Button clear-all-annotations'
      buttonNode.title = t('canvasUI.components.fileViewer.deleteAnnotationsButtonText')

      buttonNode.style.backgroundColor = 'var(--error)'
      buttonNode.style.color = 'var(--error2)'
      buttonNode.style.border = 'none'
      buttonNode.style.padding = '8px 16px'
      buttonNode.style.borderRadius = '4px'
      buttonNode.style.fontFamily = 'Roboto'
      buttonNode.style.fontSize = '14px'
      buttonNode.style.fontWeight = '500'
      buttonNode.style.textTransform = 'uppercase'
      buttonNode.style.letterSpacing = '1.25px'
      buttonNode.style.lineHeight = '1.5'
      buttonNode.style.cursor = 'pointer'
      buttonNode.style.marginRight = '12px'
      buttonNode.style.marginLeft = '16px'

      const item = {
        type: 'custom' as const,
        id: 'clear-all-annotations',
        node: buttonNode,
        title: t('canvasUI.common.clearAll'),
        onPress: async () => {
          if (!instance) return

          confirmation.danger({
            hasExit: false,
            title: t('canvasUI.components.fileViewer.deleteAnnotationsDialog.title'),
            content: t('canvasUI.components.fileViewer.deleteAnnotationsDialog.description'),
            confirmText: t('canvasUI.components.fileViewer.deleteAnnotationsDialog.confirm'),
            cancelText: t(t('canvasUI.components.fileViewer.deleteAnnotationsDialog.cancel')),
            onConfirm: async () => {
              const currentAnnotations = await getAllAnnotations(instance)
              await instance.delete(currentAnnotations)
            },
          })
        },
      }

      return [...defaultAnnotationToolbarItems, item]
    })
  }

  async function waitForInitialPdfReady(_pspdfInstance: Instance | undefined, timeoutMs = 8000): Promise<void> {
    const hasRendered = (): boolean => {
      const page = queryShadowDom('.PSPDFKit-Page[data-page-index="0"]')[0] || queryShadowDom('.PSPDFKit-Page')[0]
      if (!page) return false
      const canvases = queryShadowDom('canvas', page)
      const images = queryShadowDom('img', page)
      return canvases.length > 0 || images.length > 0
    }

    if (hasRendered()) return

    const container = document.querySelector('.PSPDFKit-Container') as HTMLElement | null
    const root = container?.shadowRoot

    if (!root) {
      // Fallback to short polling if shadow root not ready yet
      const start = Date.now()
      return new Promise((resolve) => {
        const poll = () => {
          if (hasRendered() || Date.now() - start > timeoutMs) return resolve()
          requestAnimationFrame(poll)
        }
        poll()
      })
    }

    return new Promise((resolve) => {
      const timeoutId = window.setTimeout(() => {
        observer.disconnect()
        resolve()
      }, timeoutMs)

      const observer = new MutationObserver(() => {
        if (hasRendered()) {
          clearTimeout(timeoutId)
          observer.disconnect()
          resolve()
        }
      })

      observer.observe(root, { childList: true, subtree: true })
    })
  }

  function scrollToTop() {
    const shadowDomRoot = document.querySelector('.PSPDFKit-Container')?.shadowRoot
    const scrollContainer = shadowDomRoot?.querySelector('.PSPDFKit-Scroll')
    if (scrollContainer) scrollContainer.scrollTop = 0
  }

  const closeSearch = () => {
    isSearchOpen.value = false
    instance.value?.setViewState((viewState) => viewState.set('sidebarMode', null))
  }

  const searchStateChangeEvent = () => {
    if (instance.value?.viewState.interactionMode !== 'SEARCH') {
      closeSearch()
    }
  }

  const handleKeyDownEvent = (event: Event) => {
    switch ((event as KeyboardEvent)?.key) {
      case 'ArrowDown':
      case 'PageDown':
        instance.value?.setViewState((viewState) => viewState.goToNextPage())
        break
      case 'ArrowUp':
      case 'PageUp':
        instance.value?.setViewState((viewState) => viewState.goToPreviousPage())
        break
      case 'Home':
        instance.value?.setViewState((viewState) => viewState.set('currentPageIndex', 0))
        break
      case 'End':
        instance.value?.setViewState((viewState) =>
          viewState.set('currentPageIndex', instance.value!.totalPageCount - 1),
        )
        break
      case 'Escape':
        actions.onCloseFile()
        break
      default:
    }
  }

  const annotationSelectionChangeEvent = (annotationsUnion: List<AnnotationsUnion> | undefined) => {
    const selectedAnnotation = annotationsUnion?.get(0) as AnnotationsUnion | undefined

    if (!selectedAnnotation && !isSwitchingInteractionMode.value) {
      isTextAnnotationOpen.value = false
      isAnnotationOpen.value = false
      isLinkAnnotationOpen.value = false

      return
    }

    const isText = selectedAnnotation instanceof window.PSPDFKit.Annotations.TextAnnotation
    const isLink = selectedAnnotation instanceof window.PSPDFKit.Annotations.LinkAnnotation

    isTextAnnotationOpen.value = isText
    isLinkAnnotationOpen.value = isLink
    isSearchOpen.value = false
    isAnnotationOpen.value = false
    if (isText || isLink) return
    else if (selectedAnnotation instanceof window.PSPDFKit.Annotations.InkAnnotation) {
      selectedAnnotationType.value = selectedAnnotation?.blendMode === 'multiply' ? 'freeformHighlight' : 'drawing'
    }

    if (selectedAnnotationType.value === 'eraser' && !isSwitchingInteractionMode.value) {
      isTextAnnotationOpen.value = false
      isLinkAnnotationOpen.value = false
      isSearchOpen.value = false

      return
    }

    isAnnotationOpen.value = true
  }

  const currentPageIndexChangeEvent = (event: any) => {
    currentPageIndex.value = event
    isCurrentPageChanged.value = true

    const isImpact = clientType === CLIENT_TYPE.impact
    const isSharing = clientType === CLIENT_TYPE.dsr
    if ((isImpact || isSharing) && instance.value) setOverlayItemsForEmbeddedVideos(currentPageIndex.value)
  }

  function viewStateZoomChange(zoom: number | 'FIT_TO_WIDTH' | 'FIT_TO_VIEWPORT') {
    const fitMode = zoom === 'FIT_TO_WIDTH' || typeof zoom === 'number' ? 'width' : 'viewport'

    if (zoom === 'FIT_TO_WIDTH') scrollToTop()
    updateFileViewerSettings({ fitMode })
  }

  function toggleFullscreen() {
    isFullscreen.value = !!document.fullscreenElement
  }

  const {
    onFullscreen: onPdfFullscreen,
    registerFullscreenChangeEvents,
    removeFullscreenChangeEvents,
  } = useFileViewerFullscreen({
    pdfViewerEl,
    bottomBarRef,
    instance,
    isThumbnailsOpen,
    isSearchOpen,
  })

  const mountPdf = async ({
    file,
    pageIndex,
    assetsPrefix,
    PSPDFKit,
  }: {
    file: PitcherFile | CanvasFile
    pageIndex: MiniPage['page_index']
    assetsPrefix: string
    PSPDFKit: typeof PSPDFKitType
  }) => {
    const id = 'id' in file ? file.id : file.file_id
    if (!file.pspdfkit_auth_payload || !file.pspdfkit_server_url) {
      console.info({ pspdfkit_auth_payload: file.pspdfkit_auth_payload, pspdfkit_server_url: file.pspdfkit_server_url })
      throw new Error(`[CFileViewer.use.ts] Missing auth payload or pspdfkit server url for file ${id}`)
    }

    const fileConfig = {
      documentId: jwtDecode(file.pspdfkit_auth_payload.jwt)?.document_id ?? id,
      authPayload: file.pspdfkit_auth_payload as PSPDFKitAuthPayload,
      serverUrl: file.pspdfkit_server_url,
      instant: true,
    }

    let appSearchResultsSidebar: App | undefined

    const createCustomUI = (PSPDFKit: typeof PSPDFKitType) => {
      const handleCustomSidebarMode = (containerNode: HTMLElement) => {
        unmountAppSearchResultsSidebar(appSearchResultsSidebar)
        appSearchResultsSidebar = createApp(CFileViewerSearchResultsSidebar, {
          name: 'CFileViewerSearchResultsSidebar',
          instance: instance.value,
        })
        appSearchResultsSidebar.use(i18n)
        appSearchResultsSidebar.mount(containerNode)
      }

      const handleThumbnailRender = ({
        itemContainerNode,
        item: thumbnail,
      }: {
        itemContainerNode: HTMLElement
        item: any
      }) => {
        const enablePageMetadata = computed(
          () => launchDarkly.value.enable_file_pages_metadata && clientType === CLIENT_TYPE.admin,
        )

        const buttonParentNode = itemContainerNode.parentNode?.querySelector('div > div:nth-child(2)') as HTMLDivElement

        let metadataButtonComponent: ReturnType<typeof usePageThumbnailMetadata> | undefined
        const existingMetadataButton = buttonParentNode.querySelector('.c-file-viewer-thumbnail-metadata__button')

        if (enablePageMetadata.value && !existingMetadataButton) {
          metadataButtonComponent = usePageThumbnailMetadata({
            pageIndex: thumbnail.index,
            onShowPageMetadata: actions.onShowPageMetadata,
          })

          buttonParentNode.appendChild(metadataButtonComponent.node)
          metadataButtonComponent.node.style.display = 'none' // Start hidden

          buttonParentNode.addEventListener('mouseover', () => {
            if (metadataButtonComponent) metadataButtonComponent.node.style.display = 'block'
          })

          buttonParentNode.addEventListener('mouseout', () => {
            if (metadataButtonComponent) metadataButtonComponent.node.style.display = 'none'
          })
        }
      }

      const unmountAppSearchResultsSidebar = (appSearchResultsSidebar: App | undefined) => {
        appSearchResultsSidebar?.unmount()
      }

      return {
        [PSPDFKit.UIElement.Sidebar]: {
          [PSPDFKit.SidebarMode.CUSTOM]({ containerNode }: any) {
            handleCustomSidebarMode(containerNode)
          },
          [PSPDFKit.SidebarMode.THUMBNAILS]({ containerNode }: any) {
            return {
              node: containerNode,
              onRenderItem: handleThumbnailRender,
            }
          },
        },
      }
    }

    const getAnnotationPresets = () => {
      const annotationPresets = PSPDFKit.defaultAnnotationPresets

      annotationPresets.drawing = {
        ...annotationPresets.ink,
      }

      annotationPresets.freeformHighlight = {
        ...annotationPresets.highlighter,
      }

      annotationPresets.text = {
        ...annotationPresets.text,
        font: 'Arial',
      }

      annotationPresets.link = {
        ...PSPDFKit.defaultAnnotationPresets.link,
        custom: {
          action: '',
        },
      }

      return annotationPresets
    }

    const initialViewState = new PSPDFKit.ViewState({
      scrollMode: PSPDFKit.ScrollMode.PER_SPREAD,
      layoutMode: PSPDFKit.LayoutMode.SINGLE,
      currentPageIndex: pageIndex,
      showAnnotations: clientType !== CLIENT_TYPE.dsr || !!launchDarkly.value.enable_dsr_readonly_annotations,
      showAnnotationNotes: clientType !== CLIENT_TYPE.dsr,
      enableAnnotationToolbar: clientType !== CLIENT_TYPE.dsr,
      showToolbar: true,
      readOnly: clientType === CLIENT_TYPE.dsr,
    })

    const isImpact = clientType === CLIENT_TYPE.impact
    const isSharing = clientType === CLIENT_TYPE.dsr

    instance.value = await PSPDFKit.load({
      autoSaveMode: PSPDFKit.AutoSaveMode.DISABLED,
      locale: appLocale,
      theme: 'LIGHT',
      annotationPresets: getAnnotationPresets(),
      customUI: createCustomUI(PSPDFKit) as any, // TODO: fix type
      styleSheets: [`${assetsPrefix}assets/fileViewer.css`],
      annotationToolbarItems: (_annotation, { defaultAnnotationToolbarItems }) =>
        defaultAnnotationToolbarItems.filter((item) => item.type !== 'opacity'),
      toolbarPlacement: PSPDFKit.ToolbarPlacement.TOP,
      initialViewState,
      container: unref(pdfViewerEl)!,
      enableRichText: () => true, // Note: Enable rich text editing for annotations, works with server v2023.4.1
      enableHistory: true,
      ...fileConfig,
    })

    if (clientType !== CLIENT_TYPE.impact && clientType !== CLIENT_TYPE.dsr && !!document.fullscreenElement) {
      isFullscreen.value = false
      exitFullscreen()
    }

    if (launchDarkly.value.show_clear_all_annotations_button) {
      setAnnotationToolbarItems(instance.value)
    }

    //Note: It did not work when I tried to set it in the initialViewState
    // Determine default zoom based on page orientation
    let isLandscape: boolean | null = null
    try {
      const pageInfo = instance.value ? await instance.value.pageInfoForIndex(pageIndex ?? 0) : null
      if (pageInfo) isLandscape = pageInfo.width > pageInfo.height
    } catch (e) {
      // Fallback: if pageInfoForIndex doesn't exist or fails, keep orientation as unknown
      console.warn('[CFileViewer.pdf.use.ts] Could not determine page orientation:', e)
    }

    // If user has a saved preference, use it; otherwise use orientation-based default when known;
    // fallback to FIT_TO_VIEWPORT when orientation is unknown to preserve previous behavior
    const defaultZoom = isLandscape === false ? PSPDFKit.ZoomMode.FIT_TO_WIDTH : PSPDFKit.ZoomMode.FIT_TO_VIEWPORT
    let zoom = defaultZoom
    if (fileViewerSettings.value.fitMode === 'width') {
      zoom = PSPDFKit.ZoomMode.FIT_TO_WIDTH
    } else if (fileViewerSettings.value.fitMode === 'viewport') {
      zoom = PSPDFKit.ZoomMode.FIT_TO_VIEWPORT
    }

    instance.value?.setViewState((viewState) => viewState.set('zoom', zoom))
    await cleanAnnotations(instance.value, context)
    if (isImpact && pitcherSettings?.value.file_annotations_mode === 'admin') {
      await interpolateAnnotations(instance.value, context)
    }

    currentPageIndex.value = pageIndex ?? 0

    if (isImpact || isSharing) setOverlayItemsForEmbeddedVideos(currentPageIndex.value)

    //#region add events
    registerFullscreenChangeEvents(toggleFullscreen)
    instance.value.addEventListener('viewState.currentPageIndex.change', currentPageIndexChangeEvent)
    instance.value.addEventListener('search.stateChange', searchStateChangeEvent)
    instance.value.contentDocument.addEventListener('keydown', handleKeyDownEvent)
    instance.value.addEventListener('annotationSelection.change', annotationSelectionChangeEvent as any) // it is an immutable List and NOT 'annotationSelection.change': (annotation?: AnnotationsUnion) => void;
    instance.value.addEventListener('viewState.zoom.change', viewStateZoomChange)
    instance.value.addEventListener('annotations.create', updateAnnotations)
    instance.value.addEventListener('annotations.update', updateAnnotations)
    instance.value.addEventListener('annotations.delete', updateAnnotations)
    instance.value.addEventListener('viewState.change', (_viewState, previousViewState) => {
      //viewState.currentPageIndex.change: Zoom is already changed when this event is fired
      if (isCurrentPageChanged.value) {
        isCurrentPageChanged.value = false //needs to be on the top of setViewState to prevent infinite loop
        instance.value?.setViewState((viewState) => viewState.set('zoom', previousViewState.zoom))
      }

      if (
        instance.value &&
        launchDarkly.value.show_clear_all_annotations_button &&
        (isTextAnnotationOpen.value || isLinkAnnotationOpen.value || isAnnotationOpen.value)
      ) {
        setAnnotationToolbarItems(instance.value)
      }
    })

    //#endregion

    //#region translation
    interface I18nMessages extends Record<SupportedLocale, Record<string, string>> {}

    const locale = appLocale ?? (window.navigator.language.substring(0, 2) as SupportedLocale) // TODO: fix bcp47 proper parsing
    const pspdfKitLocaleMessages = (PSPDFKit.I18n.messages as I18nMessages)[locale]

    if (pspdfKitLocaleMessages) {
      pspdfKitLocaleMessages.searchDocument = t('canvasUI.components.fileViewer.search.placeholder')
    }
    //#endregion

    //#region inject our theme css variables
    const element = queryShadowDom('.PSPDFKit-Root')?.[0] as HTMLElement | undefined

    Object.entries(themeVars.value).forEach(([key, value]) => {
      element?.style.setProperty(`--${key}`, value)
      document.documentElement.style.setProperty(`--${key}`, value)
    })
    //#endregion

    scrollToTop()

    return instance.value
  }

  const viewPdf = async (file: PitcherFile | CanvasFile | null, pageIndex: MiniPage['page_index']) => {
    if (!file) {
      console.warn('[CFileViewer.use.ts] No PDF file to open!')

      return Promise.resolve()
    }

    const fileIdForMetrics = 'id' in file ? file.id : file.file_id
    const startTimeMs = typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now()

    let cacheHit

    if (!window.PSPDFKit) {
      cacheHit = false
      const src = `${file.pspdfkit_server_url}pspdfkit-web/${launchDarkly.value?.pspdfkit_client_version || '2024.6.0'}/pspdfkit.js`
      try {
        await loadScriptStyle({ script: { src } })
      } catch (error) {
        console.error(`${error}: ${src}`)
      }
    } else {
      cacheHit = true
    }

    const PSPDFKit = window.PSPDFKit

    if (!PSPDFKit) return Promise.reject(new Error('[CFileViewer.use.ts] PDF lib is not loaded!'))

    if (PSPDFKit && instance.value) {
      PSPDFKit.unload(instance.value) //switching between files
    } else {
      console.info(`[CFileViewer.use.ts] ${cacheHit ? 'pspdfkit cache hit!' : 'pspdfkit loaded for the first time!'}`)
    }
    try {
      const result = await mountPdf({ file, pageIndex, assetsPrefix, PSPDFKit })
      // Non-blocking readiness detection: do not delay returning the instance
      waitForInitialPdfReady(instance.value)
        .then(() => {
          const endTimeMs = typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now()
          const durationMs = Math.max(0, endTimeMs - startTimeMs)
          if (durationMs > 5000) {
            window.Sentry &&
              window.Sentry.withScope((scope: any) => {
                scope.setExtra('file', 'CFileViewer.use.ts')
                scope.setExtra('fileIdForMetrics', fileIdForMetrics)
                scope.setExtra('clientType', clientType)
                scope.setExtra('initialPageIndex', pageIndex)
                scope.setExtra('cacheHit', cacheHit)
                window.Sentry.captureMessage(`[CFileViewer.use.ts] PDF loaded in ${(durationMs / 1000).toFixed(2)}s`, {
                  level: 'warning',
                })
              })
          }
        })
        .catch(() => {
          window.Sentry &&
            window.Sentry.withScope((scope: any) => {
              scope.setExtra('file', 'CFileViewer.use.ts')
              scope.setExtra('fileIdForMetrics', fileIdForMetrics)
              scope.setExtra('clientType', clientType)
              scope.setExtra('initialPageIndex', pageIndex)
              scope.setExtra('cacheHit', cacheHit)
              window.Sentry.captureMessage(`[CFileViewer.use.ts] PDF failed to load`, { level: 'error' })
            })
        })
      return result
    } catch (e) {
      return Promise.reject(e)
    }
  }

  function cleanupPspdfKit() {
    if (pdfViewerEl.value) {
      window.PSPDFKit?.unload && console.info('[CFileViewer.use.ts] destroying PSPDFKit instance')
      removeFullscreenChangeEvents(toggleFullscreen)
      instance.value?.removeEventListener('search.stateChange', searchStateChangeEvent)
      instance.value?.removeEventListener('viewState.currentPageIndex.change', currentPageIndexChangeEvent)
      instance.value?.contentDocument.removeEventListener('keydown', handleKeyDownEvent)
      instance.value?.removeEventListener('annotationSelection.change', annotationSelectionChangeEvent as any)
      instance.value?.removeEventListener('viewState.zoom.change', viewStateZoomChange)
      instance.value?.removeEventListener('annotations.create', updateAnnotations)
      instance.value?.removeEventListener('annotations.update', updateAnnotations)
      instance.value?.removeEventListener('annotations.delete', updateAnnotations)
      window.PSPDFKit?.unload?.(pdfViewerEl.value)
    }
  }

  onBeforeUnmount(() => {
    cleanupPspdfKit()
  })

  // PDF Section End

  const hasHotspotsOnCurrentPage = ref(false)
  const hotspotCache = new Map<number, boolean>() // Cache hotspot status per page

  // Function to check if current page has hotspots (LinkAnnotations) - optimized for performance
  const checkCurrentPageHotspots = async () => {
    // Only perform detection if the LaunchDarkly flag is enabled
    if (!launchDarkly.value?.enable_hotspot_visibility) {
      hasHotspotsOnCurrentPage.value = false
      return
    }

    if (!instance.value) {
      hasHotspotsOnCurrentPage.value = false
      return
    }

    // Check cache first
    if (hotspotCache.has(currentPageIndex.value)) {
      hasHotspotsOnCurrentPage.value = hotspotCache.get(currentPageIndex.value) ?? false
      return
    }

    try {
      const annotations = await instance.value.getAnnotations(currentPageIndex.value)
      const hasHotspots = annotations.some(
        (annotation) => annotation instanceof window.PSPDFKit.Annotations.LinkAnnotation,
      )

      // Cache the result
      hotspotCache.set(currentPageIndex.value, hasHotspots)
      hasHotspotsOnCurrentPage.value = hasHotspots
    } catch (error) {
      console.warn('[CFileViewer.pdf.use.ts] Error checking page hotspots:', error)
      hasHotspotsOnCurrentPage.value = false
    }
  }

  // Clear cache when annotations change
  const clearHotspotCache = () => {
    hotspotCache.clear()
  }

  // Only set up watchers if the flag is enabled
  if (launchDarkly.value?.enable_hotspot_visibility) {
    // Watch for page changes to update hotspot status
    watch(currentPageIndex, checkCurrentPageHotspots)

    watch(instance, (newInstance) => {
      if (newInstance) {
        clearHotspotCache() // Clear cache when new instance loads
        checkCurrentPageHotspots()
        // Clear cache when annotations change (only if flag is enabled)
        const handleAnnotationChange = () => {
          if (launchDarkly.value?.enable_hotspot_visibility) {
            clearHotspotCache()
          }
        }
        newInstance.addEventListener('annotations.change', handleAnnotationChange)
        newInstance.addEventListener('annotations.create', handleAnnotationChange)
        newInstance.addEventListener('annotations.update', handleAnnotationChange)
        newInstance.addEventListener('annotations.delete', handleAnnotationChange)
      }
    })

    onBeforeUnmount(() => {
      clearHotspotCache()
    })
  }

  return {
    viewPdf,
    onPdfFullscreen,
    updateFileViewerSettings,
    cleanupPspdfKit,
    resetAnnotationChanges,
    isSearchOpen,
    isThumbnailsOpen,
    isTextAnnotationOpen,
    isLinkAnnotationOpen,
    currentPageIndex,
    isAnnotationOpen,
    areAnnotationsModified,
    selectedAnnotationType,
    isSwitchingInteractionMode,
    fileViewerSettings,
    isFullscreen,
    ...linkAnnotationData,
    hasHotspotsOnCurrentPage,
  }
}
